import { useState } from "react";
import { Button, ButtonSize } from "components/Button";
import { ButtonColor } from "components/Button";
import { ButtonFill } from "components/Button";
import { Select } from "components/Select";
import { StudentsService, TutorsService, ParentsService } from "client/openapi";
import Notifications from "util/notifications";
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { recentKey, useRecentOptions } from "util/recentPeople";

type Person = {
  id: number;
  first_name: string;
  last_name: string;
  archived_at?: string | null;
};

export type UnknownContactInfo = {
  email?: string | null;
  phone_number?: string | null;
};

type UnknownContactDialogProps = {
  inboundContactInfo: UnknownContactInfo;
  /** Linked: the person now has this phone number or email. */
  onClose: () => void;
  /** Create New User for this type: the caller opens the Invite dialog. */
  onCreate: (type: UserType) => void;
  organization: {
    id: number;
    students: Person[];
    tutors: Person[];
    parents: Person[];
  };
};

type ActionType = "link" | "create";
export type UserType = "student" | "tutor" | "parent";
type Option = { value: number; label: string };

export default function UnknownContactDialog({
  inboundContactInfo,
  onClose,
  onCreate,
  organization,
}: UnknownContactDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [selected, setSelected] = useState<Option | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleActionSelect = (action: ActionType) => {
    setActionType(action);
    setStep(2);
  };

  const handleUserTypeSelect = (type: UserType) => {
    if (actionType === "create") {
      onCreate(type);
      return;
    }
    setUserType(type);
    setSelected(null);
    setStep(3);
  };

  const handleLink = async () => {
    if (!selected) return;

    setIsLoading(true);
    try {
      // The person gets this phone number or email, so the conversation
      // is theirs from now on.
      const updateData = {
        ...(inboundContactInfo.email && { email: inboundContactInfo.email }),
        ...(inboundContactInfo.phone_number && {
          phone_number: inboundContactInfo.phone_number,
        }),
      };

      switch (userType) {
        case "student":
          await StudentsService.updateStudent({
            studentId: selected.value,
            requestBody: updateData,
          });
          break;
        case "tutor":
          await TutorsService.updateTutor({
            tutorId: selected.value,
            requestBody: updateData,
          });
          break;
        case "parent":
          await ParentsService.updateParent({
            parentId: selected.value,
            requestBody: updateData,
          });
          break;
      }

      Notifications.success("Successfully processed contact information");
      onClose();
    } catch (error: any) {
      Notifications.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        What would you like to do with this contact?
      </h3>
      <div className="flex gap-4">
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleActionSelect("link")}
        >
          Link to Existing User
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleActionSelect("create")}
        >
          Create New User
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Select User Type</h3>
      <div className="flex gap-4">
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("student")}
        >
          Student
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("tutor")}
        >
          Tutor
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("parent")}
        >
          Parent
        </Button>
      </div>
      <div className="flex justify-start w-full gap-2">
        <Button
          color={ButtonColor.BLACK}
          fill={ButtonFill.NO_BORDER}
          onClick={() => {
            setStep((step - 1) as 1 | 2 | 3);
          }}
          size={ButtonSize.SMALL}
        >
          <ArrowLeftIcon /> Back
        </Button>
      </div>
    </div>
  );

  const pickable = useRecentOptions(userType ? [userType] : []);

  const renderStep3 = () => {
    const users =
      userType === "student"
        ? organization.students
        : userType === "tutor"
        ? organization.tutors
        : organization.parents;
    // Archived people are left out of pickers.
    const options: Option[] = users
      .filter((user) => !user.archived_at)
      .map((user) => ({
        value: user.id,
        label: `${user.first_name} ${user.last_name}`,
      }));

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">
          Select {userType!.charAt(0).toUpperCase() + userType!.slice(1)}
        </h3>
        <Select
          value={selected}
          onChange={(option: Option | null) => setSelected(option)}
          options={pickable.options(options, (option) =>
            recentKey(userType ?? "", option.value)
          )}
          onInputChange={pickable.onInputChange}
        />
        <div className="flex justify-between w-full gap-2">
          <Button
            color={ButtonColor.BLACK}
            fill={ButtonFill.NO_BORDER}
            onClick={() => {
              setStep((step - 1) as 1 | 2 | 3);
            }}
            size={ButtonSize.SMALL}
          >
            <ArrowLeftIcon /> Back
          </Button>
          <Button
            color={ButtonColor.DARK_BLUE}
            fill={ButtonFill.HOLLOW}
            onClick={handleLink}
            disabled={!selected || isLoading}
          >
            {isLoading ? "Processing..." : "Link Contact"}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-4">
      <div className="my-6 p-4 bg-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-500 mb-1">
          Contact Information
        </h4>
        {inboundContactInfo.email && (
          <p className="text-base">{inboundContactInfo.email}</p>
        )}
        {inboundContactInfo.phone_number && (
          <p className="text-base">{inboundContactInfo.phone_number}</p>
        )}
      </div>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
}
